/**
 * Main application component
 */

import React from 'react';
import { useAuth, useUser, useProviderLogout } from './auth/hooks';
import { getConfig } from './config';
import { EnvIndicator } from './components/EnvIndicator';
import { logAuthEvent, getStoredCorrelationId, generateCorrelationId } from './auth/logging';
import { useDevModeConfig } from './hooks/useDevModeConfig.js';
import { DevModeSettings } from './components/DevModeSettings';
import { ImportPage } from './pages/ImportPage';
import ftiLogo from './assets/fti-logo.png';
import {
  Button,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

export const App: React.FC = () => {
  const auth = useAuth();
  const user = useUser();
  const config = getConfig();
  const { signoutRedirect } = useProviderLogout();
  const devModeConfig = useDevModeConfig();
  const { theme, setTheme } = useTheme();

  // Force light theme regardless of system preference
  React.useEffect(() => {
    setTheme('light');
  }, [setTheme]);

  // Log authentication errors when they occur
  React.useEffect(() => {
    if (auth.error) {
      const correlationId = getStoredCorrelationId() || generateCorrelationId();
      logAuthEvent('auth_error', 'Authentication error occurred', {
        correlationId,
        errorType: auth.error.name,
        error: auth.error
      });
    }
  }, [auth.error]);

  const handleSignIn = () => {
    const correlationId = getStoredCorrelationId() || generateCorrelationId();
    logAuthEvent('auth_attempt', 'User initiated sign-in flow', {
      correlationId
    });
    auth.signinRedirect();
  };

  const isProdEnvironment = config.name === 'prod';
  const errorColor = theme.colors.semantic.error.default;

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: theme.colors.background.default,
        color: theme.colors.text.primary,
      }}
    >
      {/* Production Warning Banner */}
      {isProdEnvironment && (
        <div
          style={{
            padding: `${spacing.sm} ${spacing.md}`,
            background: errorColor,
            color: theme.colors.text.inverse,
            textAlign: 'center',
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          PRODUCTION ENVIRONMENT - Actions affect real data
        </div>
      )}

      {/* Top bar */}
      <header
        style={{
          padding: `${spacing.sm} ${spacing.lg}`,
          borderBottom: `1px solid ${theme.colors.border.default}`,
          background: theme.colors.background.paper,
        }}
      >
        <Flex justify="between" align="center">
          <Flex align="center" gap="md">
            <img src={ftiLogo} alt="FTI Consulting" style={{ height: '24px' }} />
            <h1
              style={{
                fontSize: '1.1rem',
                fontWeight: 600,
                margin: 0,
                color: theme.colors.text.primary,
              }}
            >
              Chart Studio
            </h1>
            <EnvIndicator
              environmentName={config.name}
              displayName={config.displayName}
              isMock={config.isMock}
              apiTarget={devModeConfig.isDevModeAvailable ? devModeConfig.config.apiTarget : undefined}
            />
          </Flex>

          <Flex align="center" gap="sm">
            <DevModeSettings />
            {auth.isAuthenticated && user?.email && (
              <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
                {user.email}
              </span>
            )}
            {auth.isAuthenticated && (
              <Button variant="outline" color="neutral" size="sm" onClick={() => signoutRedirect()}>
                Sign out
              </Button>
            )}
            {!auth.isAuthenticated && !auth.isLoading && (
              <Button variant="solid" color="primary" size="sm" onClick={handleSignIn}>
                Sign in
              </Button>
            )}
          </Flex>
        </Flex>
      </header>

      {/* Main content */}
      <div style={{ flex: 1 }}>
        {auth.isAuthenticated && <ImportPage />}

        {!auth.isAuthenticated && !auth.isLoading && (
          <Flex justify="center" align="center" style={{ padding: spacing.xl }}>
            <p style={{ color: theme.colors.text.secondary }}>
              Sign in to get started.
            </p>
          </Flex>
        )}
      </div>
    </main>
  );
};
